import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export function PasswordInput({ icon: Icon, className = "", inputCls = "", ...props }) {
  const [visible, setVisible] = useState(false);
  const toggle = () => setVisible((v) => !v);
  return (
    <div className={`relative ${className}`}>
      {Icon && <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />}
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={`${Icon ? "pl-10" : ""} pr-11 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans ${inputCls}`}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded text-white/40 hover:text-[#D4AF37] focus:text-[#D4AF37] focus:outline-none transition-colors [&_svg]:pointer-events-none"
        data-testid={`${props["data-testid"] || "password"}-toggle`}
        tabIndex={0}
      >
        {visible ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
      </button>
    </div>
  );
}


function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", color: "transparent", percent: 0 };
  let score = 0;
  const checks = {
    length8: pw.length >= 8,
    length12: pw.length >= 12,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  if (checks.length8) score += 1;
  if (checks.length12) score += 1;
  if (checks.lower && checks.upper) score += 1;
  if (checks.digit) score += 1;
  if (checks.symbol) score += 1;

  const map = [
    { label: "Too short", color: "bg-red-500/70", text: "text-red-300", percent: 15 },
    { label: "Weak", color: "bg-red-500/70", text: "text-red-300", percent: 30 },
    { label: "Fair", color: "bg-amber-400/80", text: "text-amber-300", percent: 50 },
    { label: "Good", color: "bg-yellow-400/80", text: "text-yellow-200", percent: 72 },
    { label: "Strong", color: "bg-emerald-500/80", text: "text-emerald-300", percent: 88 },
    { label: "Excellent", color: "bg-emerald-500", text: "text-emerald-200", percent: 100 },
  ];
  return { score, ...map[Math.min(score, map.length - 1)] };
}

export function PasswordStrength({ value }) {
  const s = scorePassword(value);
  if (!value) return null;
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${s.color}`}
          style={{ width: `${s.percent}%` }}
        />
      </div>
      <p className={`text-[10px] uppercase tracking-[0.25em] mt-1.5 font-sans ${s.text}`} data-testid="password-strength-label">
        {s.label}
      </p>
    </div>
  );
}
